import { bodyCopyBold, ISelectOption, Modal, Select } from '@aon/aon-tws-common-ui';
import _ from 'lodash';
import React, { useState } from 'react';
import HtmlParser from 'html-react-parser';
import styled from 'styled-components';
import ContentConstants from '../../../contentConstants';
import { ISurveyDimension, ISurveyQuestionEditModel } from '../../../models';
import { addSurveyQuestionsToDimension, moveSurveyQuestionsToNewDimension } from '../../../store/SurveyDimensionsSlice';
import { useAppDispatch } from '../../../appStore';

export interface IMoveQuestionsModalProps {
	isOpen: boolean;
	surveyId: number;
	sourceSurveyDimensionId?: number;
	questionsToMove: ISurveyQuestionEditModel[];
	availableDimensions: ISurveyDimension[];
	isSaving: boolean;
	onCancel: () => void;
	onSubmit: () => void;
}

const CustomizedMoveQuestionsModal = styled(Modal)`
	.modal-body {
		flex-direction: column;
		gap: 3em;
		overflow: visible;

		strong {
			${bodyCopyBold}
		}
	}
`;

const MoveQuestionsModal = ({
	isOpen,
	surveyId,
	sourceSurveyDimensionId,
	questionsToMove,
	availableDimensions,
	isSaving,
	onSubmit,
	onCancel,
}: IMoveQuestionsModalProps) => {
	const [selectedDimensionOrIndexId, setSelectedDimensionOrIndexId] = useState<number>();
	const dispatch = useAppDispatch();

	const questionsHaveMultipleResponseOptionGroups = _.uniq(questionsToMove.map((q) => q.sourceResponseOptionGroupId)).length > 1;
	const selectOptions = availableDimensions.map(
		(dimension, index) => ({ displayOrder: index, name: dimension.dimensionId?.toString(), displayText: dimension.name } as ISelectOption),
	);

	const handleClose = () => {
		setSelectedDimensionOrIndexId(undefined);
		onCancel();
	};

	const handleSubmit = async () => {
		if (sourceSurveyDimensionId) {
			await dispatch(
				moveSurveyQuestionsToNewDimension({
					surveyId,
					sourceSurveyDimensionId,
					targetSurveyDimensionId: selectedDimensionOrIndexId!,
					questionsToMove,
				}),
			);
		} else {
			await dispatch(
				addSurveyQuestionsToDimension({
					surveyId,
					surveyDimensionId: selectedDimensionOrIndexId!,
					surveyQuestions: questionsToMove,
				}),
			);
		}

		onSubmit?.();
	};

	return (
		<CustomizedMoveQuestionsModal
			isOpen={isOpen}
			title={ContentConstants.TITLE_MOVE_QUESTIONS}
			primaryButtonText={ContentConstants.BUTTONS_MOVE_QUESTIONS}
			primaryButtonDisabled={selectedDimensionOrIndexId === undefined || isSaving}
			onSubmit={handleSubmit}
			secondaryButtonText={ContentConstants.BUTTONS_CANCEL}
			onSecondaryButtonClick={handleClose}
			secondaryButtonDisabled={isSaving}
			onClosed={handleClose}
			contenthcentered={true}
			contentvcentered={true}
			contentfullwidth={false}
			type='lightbox'
		>
			{selectOptions.length > 0 ? (
				<>
					<div data-testid='main-content'>
						{questionsHaveMultipleResponseOptionGroups
							? HtmlParser(
									ContentConstants.INSTRUCTIONALTEXT_MOVE_QUESTIONS_IN_MULTIPLE_RESPONSE_GROUPS_TO_NEW_DIMENSION.replace(
										'{questionCount}',
										questionsToMove.length.toString(),
									),
							  )
							: ContentConstants.INSTRUCTIONALTEXT_MOVE_QUESTIONS_IN_SINGLE_RESPONSE_GROUP_TO_NEW_DIMENSION.replace(
									'{questionCount}',
									questionsToMove.length.toString(),
							  )}
					</div>
					<div>
						<Select
							size='lg'
							selectSize='lg'
							dropdownSize='lg'
							items={selectOptions}
							displayText='Select'
							onApply={(value) => setSelectedDimensionOrIndexId(parseInt(value as string, 10))}
							selectedValue={selectedDimensionOrIndexId?.toString()}
						/>
					</div>
				</>
			) : (
				<div>
					{ContentConstants.INSTRUCTIONALTEXT_MOVE_QUESTIONS_NO_AVAILABLE_DIMENSIONS_MATCH_CRITERIA.replace(
						'{questionCount}',
						questionsToMove.length.toString(),
					)}
				</div>
			)}
		</CustomizedMoveQuestionsModal>
	);
};

export default MoveQuestionsModal;
